import { Stack } from '@mui/material';
import { ComponentProps, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { EducationExportButton } from './Education.components';
import { EducationListItemType } from './Education.constants';
import { EducationList } from './Education.List';
import { EducationSlideDisplay } from './Education.SlideDisplay';
import { isActiveEducationSlideValid } from './Education.utils';
import {
  useEducationMap,
  useEducationSlideOverrides,
} from './EducationSlideOverrides/EducationSlideOverrides.context';
import { isCustomSlideIdentifier } from './EducationSlideTrowser/utils/slideIdentifier.utils';

interface EducationProps {
  householdId: string;
}

type Crumb = ComponentProps<typeof BigBreadcrumbs>['crumbs'][number];

export function Education({ householdId }: EducationProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { customSlidesMap } = useEducationSlideOverrides();
  const educationMap = useEducationMap();

  // The active slide can be either a template slide ID or a custom slide identifier
  const activeSlideParam = searchParams.get('activeSlide');

  const activeSlide = useMemo<EducationListItemType | null>(
    () =>
      isActiveEducationSlideValid(activeSlideParam)
        ? (activeSlideParam as EducationListItemType)
        : null,
    [activeSlideParam]
  );

  // Check if active slide is a custom slide
  const activeCustomSlideIdentifier = useMemo(
    () =>
      activeSlideParam && isCustomSlideIdentifier(activeSlideParam)
        ? activeSlideParam
        : null,
    [activeSlideParam]
  );

  const body = useMemo(
    () =>
      activeSlide ? (
        <EducationSlideDisplay activeSlide={activeSlide} />
      ) : activeCustomSlideIdentifier ? (
        <EducationSlideDisplay
          activeSlide={null}
          customSlideIdentifier={activeCustomSlideIdentifier}
        />
      ) : (
        <EducationList
          onClickListItem={(id: EducationListItemType) => {
            setSearchParams({ activeSlide: id });
          }}
          onClickCustomSlide={(slideIdentifier: string) => {
            setSearchParams({ activeSlide: slideIdentifier });
          }}
        />
      ),
    [activeSlide, activeCustomSlideIdentifier, setSearchParams]
  );

  const breadcrumbs = useMemo<Crumb[]>(() => {
    const crumbs: Crumb[] = [
      {
        label: 'Education',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.EDUCATION, {
          householdId,
        }),
      },
    ];

    if (!activeSlide && !activeCustomSlideIdentifier) return crumbs;

    // Get title from template slide or custom slide
    let label: string | undefined;
    if (activeSlide) {
      label = educationMap[activeSlide]?.title;
    } else if (activeCustomSlideIdentifier) {
      label =
        customSlidesMap[activeCustomSlideIdentifier]?.displayName ||
        'Custom page';
    }

    if (label) {
      crumbs.push({
        label,
      });
    }

    return crumbs;
  }, [
    activeSlide,
    activeCustomSlideIdentifier,
    customSlidesMap,
    householdId,
    educationMap,
  ]);

  return (
    <Stack
      display="flex"
      height={'100%'}
      overflow="auto"
      bgcolor="common.white"
    >
      <PageHeader
        heading={<BigBreadcrumbs crumbs={breadcrumbs} />}
        action={
          <EducationExportButton
            activeSlide={activeSlide}
            customSlideIdentifier={activeCustomSlideIdentifier}
          />
        }
      />
      {body}
    </Stack>
  );
}
